import moment from 'moment-timezone';
import _ from 'lodash';
import { List } from 'immutable';

import config from '../config';
import User from '../records/User';
import trainTime from '../lib/TrainTime';
import BaseStore from './BaseStore';
import { ActionTypes } from '../constants/AppConstants';
import CookieHelper from '../helpers/Cookie';
import InAppStore from "./InAppStore";
import useStoreState from './useStoreState';
import debugHook from '../hooks/hookUtils';

let currentUser = null;
let lastRefreshed = null;
let autoLogoutWarn = false;
let autoLogout = false;

const USER_COOKIE_KEY = 'Customer-Token';
const LAST_USER_ACTIVITY_KEY = 'last-user-activity';

export class UserStore extends BaseStore {

    constructor () {
        super();
       _.defer(() => this._initialiseAutoLogout());
    }

    getActionHandlers () {
        return {
            [ActionTypes.AUTH_LOADED]: this.handleAuthUpdated,
            [ActionTypes.USER_UPDATED]: this.handleUserUpdated,
            [ActionTypes.AUTH_REFRESH_STARTED]: this.handleAuthRefreshStarted,
            [ActionTypes.AUTH_REFRESHED]: this.handleAuthUpdated,
            [ActionTypes.LOGGED_IN]: this.handleUserUpdated,
            [ActionTypes.LOGGED_OUT]: this.handleLoggedOut,
            [ActionTypes.REGISTRATION_SUCCESS]: this.handleUserUpdated,
            [ActionTypes.REGISTRATION_FAILED]: this.handleRegistrationFailed,
            [ActionTypes.REGISTRATION_STARTED]: this.handleRegistrationStarted,
            [ActionTypes.SMARTCARD_REGISTERED]: this.handleSmartcardRegistered,
            [ActionTypes.USER_OCDR_SUBSCRIBE_SUCCESS]: this.handleOneClickDelayRepayOptIn,
            [ActionTypes.USER_OCDR_UNSUBSCRIBE_SUCCESS]: this.handleOneClickDelayRepayOptOut,
            [ActionTypes.SMARTCARD_LINKED]: this.handleSmartcardRegistered,
            [ActionTypes.ACTIVITY_DETECTED]: this.updateLastUserActivity,
            [ActionTypes.ADDRESSES_LOADED]: this.handleAddressesLoaded,
            [ActionTypes.ADDRESS_CREATED]: this.handleAddressCreated,
            [ActionTypes.ADDRESS_DELETED]: this.handleAddressDeleted,
            [ActionTypes.ADDRESS_UPDATED]: this.handleAddressUpdated,
            [ActionTypes.USER_PREFERENCES_UPDATED]: this.handlePreferencesUpdated,
            [ActionTypes.USER_PREFERENCES_FETCHED]: this.handlePreferencesFetched,
            [ActionTypes.CHECK_USER_COOKIE]: this.checkUserCookie,
        };
    }

    getCurrent () {
        if (!currentUser) {
            currentUser = CookieHelper.objectFromCookie(USER_COOKIE_KEY, User) || new User();
        }
        return currentUser;
    }

    setCurrent (user) {
        if (!user) {
            return false;
        }
        const userWithoutAddresses = user.setIn(['customer', 'addresses'], new List());
        currentUser = user;
        const authToken = user.authToken;
        if (authToken && user.id) {
            if (InAppStore.isInApp()) {
                const expires = trainTime(authToken.expires).format();
                CookieHelper.objectToCookie(USER_COOKIE_KEY, userWithoutAddresses, expires);
            } else {
                CookieHelper.objectToCookie(USER_COOKIE_KEY, userWithoutAddresses);
            }
        }
        this.emitChange();

        return true;
    }

    getIsRegistering() {
        return this.isRegistering;
    }

    setIsRegistering(isRegistering) {
        this.isRegistering = isRegistering;
        this.emitChange();
    }

    getIsRefreshingAuthToken() {
        return this.isRefreshingAuthToken;
    }

    setIsRefreshingAuthToken(isRefreshingAuthToken) {
        this.isRefreshingAuthToken = isRefreshingAuthToken;
        this.emitChange();
    }

    checkUserCookie () {
        if (CookieHelper.objectFromCookie(USER_COOKIE_KEY, User)) {
            location.reload();
        }
    }

    handleUserUpdated ({ user }) {
        lastRefreshed = trainTime();
        this.setIsRegistering(false);
        this.setCurrent(user);
        this.updateLastUserActivity();
    }

    handlePreferencesUpdated({ preferences, isOver16, telephoneNumber }) {
        const updated = this.getCurrent().setPreferences(preferences)
            .setIsOver16(isOver16)
            .setTelephoneNumber(telephoneNumber);
        this.setCurrent(updated);
    }

    handlePreferencesFetched({ preferences }) {
        this.setCurrent(this.getCurrent().setPreferences(preferences));
    }

    handleAuthRefreshStarted () {
        this.setIsRefreshingAuthToken(true);
    }

    handleAuthUpdated ({ token }) {
        const user = this.getCurrent().setAuthToken(token);
        this.handleUserUpdated({ user });
        this.setIsRefreshingAuthToken(false);
    }

    handleLoggedOut () {
        currentUser = null;
        lastRefreshed = null;
        CookieHelper.clear(USER_COOKIE_KEY);
        this._resetAutoLogoutFlags();
        this.setIsRefreshingAuthToken(false);
        this.emitChange();
    }

    handleSmartcardRegistered () {
        this.setCurrent(this.getCurrent().setHasRegisteredSmartcard(true));
    }

    handleOneClickDelayRepayOptIn() {
        this.setCurrent(this.getCurrent().setOneClickDelayRepayOptedIn(true));
    }

    handleOneClickDelayRepayOptOut() {
        this.setCurrent(this.getCurrent().setOneClickDelayRepayOptedIn(false));
    }

    shouldBeRefreshed () {
        const user = this.getCurrent();
        if (user.isLoggedIn()) {
            const today = trainTime().startOf('day');
            return (!lastRefreshed || today.isAfter(lastRefreshed));
        }
        return false;
    }

    shouldAutoLogout () {
        return autoLogout && !InAppStore.disableAutoLogout() && config.get('sessionShort');
    }

    shouldAutoLogoutWarn () {
        return autoLogoutWarn && !InAppStore.disableAutoLogout() && config.get('sessionShort');
    }

    updateLastUserActivity () {
        if (this.getCurrent().isLoggedIn()) {
            const expires = moment().add(config.get('autoLogoutMinutes'), 'minutes');
            CookieHelper.objectToCookie(LAST_USER_ACTIVITY_KEY, moment().format(), expires.format());

            if (autoLogoutWarn) {
                autoLogoutWarn = false;
                this.emitChange();
            }

            this._setInactivityWarning();
            this._setInactivityLogout();
        }
    }

    _initialiseAutoLogout () {
        if (this.getCurrent().isLoggedIn()) {
            const lastActivityStored = CookieHelper.objectFromCookie(LAST_USER_ACTIVITY_KEY);
            if (!lastActivityStored) {
                autoLogout = true;
            }
        }

        if (!autoLogout) {
           this.updateLastUserActivity();
        }
    }

    _setInactivityWarning = _.debounce(() => {
        if (this.getCurrent().isLoggedIn()) {
            autoLogoutWarn = true;
            this.emitChange();
        }
    }, config.get('autoLogoutWarningMinutes') * 60 * 1000);

    _setInactivityLogout = _.debounce(() => {
        if (this.getCurrent().isLoggedIn()) {
            autoLogout = true;
            this.emitChange();
        }
    }, config.get('autoLogoutMinutes') * 60 * 1000);

    _resetAutoLogoutFlags () {
        this._setInactivityLogout.cancel();
        this._setInactivityWarning.cancel();
        CookieHelper.clear(LAST_USER_ACTIVITY_KEY);
        autoLogoutWarn = false;
        autoLogout = false;
    }

    handleAddressesLoaded({ addresses }) {
        this.setCurrent(this.getCurrent().setAddresses(addresses));
    }

    handleAddressCreated({ address }) {
        const addresses = this.getCurrent().addresses.push(address);
        this.setCurrent(this.getCurrent().setAddresses(addresses));
    }

    handleAddressDeleted({ address }) {
        const addresses = this.getCurrent().addresses;
        const index = addresses.indexOf(address);
        this.setCurrent(this.getCurrent().setAddresses(addresses.delete(index)));
    }

    handleAddressUpdated({ updatedAddress }) {
        const addresses = this.getCurrent().addresses;
        const index = addresses.findIndex((x) => x.id === updatedAddress.id);
        this.setCurrent(
            this.getCurrent().setAddresses(addresses.set(index, updatedAddress))
        );
    }

    handleRegistrationStarted() {
        this.setIsRegistering(true);
    }

    handleRegistrationFailed() {
        this.setIsRegistering(false);
    }
}

const storeInstance = new UserStore();

/**
 * Hook to access the current logged-in user
 * @returns {User}
 */
export const useCurrentUser = debugHook(function _useCurrentUser() {
    const user = useStoreState(storeInstance, storeInstance.getCurrent);
    return user && user.isFullyLoaded() ? user : null;
}, 'useCurrentUser');


export default storeInstance;
